import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import { PermissionAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import PDFViewer from '../components/document/PDFViewer';
import { ShareDocumentDialog } from '../components/permission';

interface DocumentMetadata {
  documentId: string;
  name: string;
  pageCount: number;
  fileSize: number;
  createdAt: string;
  ownerEmail: string;
  isOwner: boolean;
}

interface UserPermissions {
  canView: boolean;
  canDownload: boolean;
  canPrint: boolean;
  allowedPages?: number[];
  deniedPages?: number[];
}

const DocumentViewerPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const [metadata, setMetadata] = useState<DocumentMetadata | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareCount, setShareCount] = useState(0);

  const { documents } = useApi();
  const navigate = useNavigate();

  // Transform API response format to frontend interface format
  const transformDocumentMetadata = (apiData: any): DocumentMetadata => ({
    documentId: apiData.document_id,
    name: apiData.name,
    pageCount: apiData.page_count,
    fileSize: apiData.file_size,
    createdAt: apiData.created_at,
    ownerEmail: apiData.owner_email || 'Unknown',
    isOwner: apiData.is_owner ?? false,
  });

  useEffect(() => {
    if (!documentId) {
      navigate('/dashboard');
      return;
    }

    loadDocumentMetadata();
  }, [documentId]);

  const loadDocumentMetadata = async () => {
    try {
      setLoading(true);
      setError('');

      const metadataApiData = await documents.getDocumentMetadata(documentId!);

      setMetadata(transformDocumentMetadata(metadataApiData));

      // Fetch share count for owner
      if (metadataApiData.is_owner) {
        PermissionAPI.getDocumentPermissions(documentId!).then((data: any) => {
          setShareCount((data.permissions || []).length);
        }).catch(() => {});
      }

      // Permissions are returned by the access lambda's metadata endpoint for the current user
      const p = metadataApiData.permissions;
      setPermissions({
        canView: p?.can_view ?? true,
        canDownload: p?.can_download ?? true,
        canPrint: p?.can_print ?? true,
        allowedPages: p?.allowed_pages || undefined,
        deniedPages: p?.denied_pages || undefined,
      });
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('You do not have permission to view this document.');
      } else if (err.response?.status === 404) {
        setError('Document not found.');
      } else {
        setError(err.response?.data?.message || 'Failed to load document');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <LoadingSpinner message="Loading document..." />;
  }

  if (error) {
    return (
      <div className="page-container">
        <ErrorMessage message={error} />
        <div className="text-center mt-4">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!metadata) {
    return (
      <div className="page-container">
        <ErrorMessage message="Document metadata not available" />
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Document Header */}
      <div style={{ 
        background: '#fff', 
        borderBottom: '1px solid #e9ecef', 
        padding: '15px 20px',
        flexShrink: 0
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{metadata.name}</h2>
            <div className="text-muted" style={{ fontSize: '14px' }}>
              {formatFileSize(metadata.fileSize)} • {metadata.pageCount} pages • 
              Uploaded {formatDate(metadata.createdAt)} by {metadata.ownerEmail}
            </div>
          </div>
          
          <div className="d-flex" style={{ gap: '10px' }}>
            {metadata.isOwner && (
              <>
                <button className="btn btn-secondary share-btn-with-badge" onClick={() => setShowShareDialog(true)}>
                  Share
                  {shareCount > 0 && (
                    <span className="share-count-badge">{shareCount}</span>
                  )}
                </button>
                <Link to={`/documents/${documentId}/analytics`} className="btn btn-secondary">
                  Analytics
                </Link>
              </>
            )}
            <button
              className="btn btn-primary"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <PDFViewer 
          documentId={documentId!}
          pageCount={metadata.pageCount}
          isAnonymous={false}
          permissions={permissions || undefined}
        />
      </div>

      <ShareDocumentDialog
        documentId={documentId!}
        documentName={metadata.name}
        isOpen={showShareDialog}
        onClose={() => setShowShareDialog(false)}
      />
    </div>
  );
};

export default DocumentViewerPage;